/**
 * prominentTips — how many TAILS leave one root: the far ends of a distance
 * field that stand clear of each other (Allen's kitsune, 2026-09-24: five
 * bushy tails fanned from one sacrum, fused near the base; the zone took all
 * five and T2 grew ONE chain down one of them).
 *
 * A tail's tip is a local maximum of the geodesic distance from where the
 * flesh meets the body. A fur spike, a fold or a lumpy brush makes maxima
 * too, but shallow ones: descend a few centimetres and they join the tail
 * they sit on. A sibling tail has to be descended most of its length before
 * it meets the others. That depth is the maximum's PERSISTENCE (0-dimensional
 * persistence of the super-level sets): sweep the nodes from far to near,
 * union-find the swept ones, and when two components meet, the younger (its
 * peak is the lower one) dies, having persisted `peak − meeting level`.
 *
 * A tip counts when it is FAR (at least `minFar` of the farthest distance —
 * a lobe of body near the junction is no tail) and PROMINENT (it persisted at
 * least `minPersistence` of the farthest distance). The farthest tip always
 * counts. Kernel-pure: a distance per node and a neighbour list in, counts out.
 *
 * Depends on: nothing. Depended on by: zones/tail-detect (a detected zone's
 * fan count), tails/tip (T2 refuses one chain across a fan).
 */

/** Far enough from the body to be a tail's end: this fraction of the farthest distance. */
export const TIP_MIN_FAR = 0.5;
/** Deep enough to be its own tail: persisted this fraction of the farthest distance. */
export const TIP_MIN_PERSISTENCE = 0.15;
/**
 * …and PARTED from the others no farther out than this fraction of the farthest
 * distance. A fan's tails part near the root (the kitsunes: at 0.41–0.47 and
 * 0.70–0.72 of the length); a frayed brush's strands part near the END
 * (ponyBoy's horse tail, 2026-09-25: 0.77–0.91 — one strand persisted 0.153 and
 * read as a second tail, which hid its "centre"). Measured; the margin is thin.
 */
export const TIP_MAX_JOIN = 0.75;

export interface ProminentTips {
  /** Node index of each counted tip, farthest first. */
  readonly tips: readonly number[];
  /** How far each counted tip persisted (in the field's units), same order. */
  readonly persistence: readonly number[];
  /** The farthest finite distance in the field. */
  readonly farthest: number;
}

/**
 * `distance[i]` is node i's distance from the root (non-finite = not reached,
 * ignored); `neighbours(i)` lists its graph neighbours.
 */
export function prominentTips(
  distance: ArrayLike<number>,
  neighbours: (i: number) => Iterable<number>,
  options: { readonly minFar?: number; readonly minPersistence?: number; readonly maxJoin?: number } = {},
): ProminentTips {
  const minFar = options.minFar ?? TIP_MIN_FAR;
  const minPersistence = options.minPersistence ?? TIP_MIN_PERSISTENCE;
  const maxJoin = options.maxJoin ?? TIP_MAX_JOIN;
  const order: number[] = [];
  let farthest = 0;
  for (let i = 0; i < distance.length; i++) {
    const d = distance[i]!;
    if (!Number.isFinite(d)) continue;
    order.push(i);
    if (d > farthest) farthest = d;
  }
  // Far to near; ties by index so the sweep is deterministic.
  order.sort((a, b) => distance[b]! - distance[a]! || a - b);
  const parent = new Map<number, number>();
  /** Each component's peak node (its eldest). */
  const peak = new Map<number, number>();
  const find = (i: number): number => {
    let r = i;
    while (parent.get(r) !== r) r = parent.get(r)!;
    let c = i;
    while (c !== r) {
      const next = parent.get(c)!;
      parent.set(c, r);
      c = next;
    }
    return r;
  };
  const died = new Map<number, number>();
  /** Each live component's nearest level so far (the sweep only descends). */
  const low = new Map<number, number>();
  for (const i of order) {
    parent.set(i, i);
    peak.set(i, i);
    low.set(i, distance[i]!);
    for (const j of neighbours(i)) {
      if (!parent.has(j)) continue;
      const a = find(i);
      const b = find(j);
      if (a === b) continue;
      const pa = peak.get(a)!;
      const pb = peak.get(b)!;
      // The elder (higher peak) survives; the younger dies at this level.
      const elderIsA = distance[pa]! > distance[pb]! || (distance[pa]! === distance[pb]! && pa < pb);
      const [elder, younger] = elderIsA ? [a, b] : [b, a];
      const youngPeak = peak.get(younger)!;
      died.set(youngPeak, distance[youngPeak]! - distance[i]!);
      parent.set(younger, elder);
    }
    low.set(find(i), distance[i]!);
  }
  const counted: { tip: number; persistence: number }[] = [];
  for (const i of order) {
    if (peak.get(i) !== i) continue;
    // Only a component's own peak ever dies or survives.
    const p = died.get(i);
    if (p === undefined && find(i) !== i) continue;
    // A survivor never met an elder: it is the peak of a component of its own
    // (the farthest tip's, or an island the field reached apart). It persisted
    // down to its component's nearest level — the farthest tip always counts.
    const persistence = p === undefined ? distance[i]! - low.get(i)! : p;
    const isFarthest = distance[i]! === farthest && p === undefined;
    // Where it joined the others: its peak less its persistence (a fraying strand joins near the end).
    const joined = distance[i]! - persistence;
    if (!isFarthest && (distance[i]! < minFar * farthest || persistence < minPersistence * farthest || joined > maxJoin * farthest)) continue;
    counted.push({ tip: i, persistence });
  }
  counted.sort((a, b) => distance[b.tip]! - distance[a.tip]! || a.tip - b.tip);
  return { tips: counted.map((c) => c.tip), persistence: counted.map((c) => c.persistence), farthest };
}
